import React, { useContext, useEffect } from "react";
import { toastSuccess, toastError } from "../../../utils";
import "./style/CreateAndUpdateBlockPlanning.scss";
import { patchService, postService } from "../../../services/axios/services";
import FormAndFields from "./FormAndFields/FormAndFields";
import AppContext from "../../../context/appContext";
import SheetContext from "../../../context/sheetContext";

const CreateAndUpdateMaterialsPlanning = (props) => {
  const {
    action,
    planningData,
    list,
    contentId,
    getPlanning,
    schedulerType,
    handleChangeByInputName,
    stateCreateOrEdit,
    resetCreateOrEdit,
  } = props;
  const appContext = useContext(AppContext);
  const projectSheetContext = useContext(SheetContext);

  const isChapter = schedulerType === "Capítulos/Sinopse";

  const creationType = isChapter
    ? Number(stateCreateOrEdit.typeId) !== 1211
      ? "materials"
      : "blocks"
    : "materials";

  const editType = isChapter
    ? stateCreateOrEdit.editType === "block"
      ? "blocks"
      : "materials"
    : "materials";

  const key = action === "create" ? creationType : editType;

  const createOnlyFlow = () => {
    appContext.setShowGlassLoading(false);
    toastSuccess(planningData.messages.postMessages.success);
    appContext.setCurrentStepBox("closed");
    getPlanning(`createOnlyFlow`);
  };

  const saveAndCreateNewFlow = () => {
    appContext.setShowGlassLoading(false);
    toastSuccess(planningData.messages.postMessages.success);
    getPlanning(`saveAndCreateNewFlow`);
  };

  const handleCreateSubmit = (event, refreshOnSuccess = true) => {
    event.preventDefault();
    appContext.setShowGlassLoading(true);

    const endpoint = planningData.endpoints.post({ key });

    const postObj = planningData?.postBody({
      key,
      payload: {
        ...stateCreateOrEdit,
        contentId,
        typeId: parseInt(stateCreateOrEdit.typeId),
        blockNumber: planningData?.nextValidBlock,
        episodeNumber:
          parseInt(stateCreateOrEdit.typeId) !== 1211
            ? null
            : parseInt(stateCreateOrEdit.chapterNumber) || 1,
        chapterNumber:
          parseInt(stateCreateOrEdit.typeId) !== 1211
            ? null
            : parseInt(stateCreateOrEdit.chapterNumber) || 1,
        numberChaptersBlock: isChapter
          ? (stateCreateOrEdit.numberChaptersBlock ?? 6)
          : stateCreateOrEdit.numberChaptersBlock,
        delivery: stateCreateOrEdit.planning.delivery || null,
        release: stateCreateOrEdit.planning.release || null,
        contentAssessment: stateCreateOrEdit.planning.contentAssessment || null,
        productionAssessment:
          stateCreateOrEdit.planning.productionAssessment || null,
        diversityAssessment:
          stateCreateOrEdit.planning.diversityAssessment || null,
        channelNotes: stateCreateOrEdit.planning.channelNotes || null,
        version: stateCreateOrEdit?.version || 1,
      },
    });

    postService({ endpoint: endpoint, postData: postObj, api: "v2" })
      .then(() => {
        if (refreshOnSuccess) return createOnlyFlow();
        saveAndCreateNewFlow();
      })
      .catch(() => {
        appContext.setShowGlassLoading(false);
        toastError(planningData.messages.postMessages.error);
      });
  };

  const handleStartDates = {
    create: {
      scheduleDelivery: "",
      scheduleRelease: stateCreateOrEdit.scheduleDelivery,
    },
    edit: {
      scheduleDelivery: "",
      rescheduledDelivery: stateCreateOrEdit.scheduleDelivery,
      scheduleRelease: stateCreateOrEdit.scheduleDelivery,
      rescheduledRelease:
        stateCreateOrEdit.scheduleRelease ||
        stateCreateOrEdit.rescheduledDelivery ||
        stateCreateOrEdit.scheduleDelivery,
      plannedContentSight: "",
      plannedProdutionSight: "",
      plannedArtisticApproval: "",
      plannedChannelFeedback: "",
    },
  };

  const handleMaxDates = {
    create: {
      scheduleDelivery: stateCreateOrEdit.scheduleRelease,
      scheduleRelease: "",
    },
    edit: {
      scheduleDelivery:
        stateCreateOrEdit.rescheduledDelivery ||
        stateCreateOrEdit.scheduleRelease ||
        stateCreateOrEdit.rescheduledRelease,
      rescheduledDelivery: stateCreateOrEdit.rescheduledRelease,
      scheduleRelease: stateCreateOrEdit.rescheduledRelease,
      rescheduledRelease: "",
      plannedContentSight: "",
      plannedProdutionSight: "",
      plannedArtisticApproval: "",
      plannedChannelFeedback: "",
    },
  };

  const contentFormat = projectSheetContext.list.result.format.name;

  const getMaxChaptersByBlock = (formatName) => {
    const formatMap = {
      vertical: 30,
      novela: 10,
    };

    const normalizedFormat = (formatName || "").toLowerCase();

    return formatMap[normalizedFormat] || 10;
  };

  const maxChaptersByBlock = getMaxChaptersByBlock(contentFormat);

  const handleEditSubmit = (ev) => {
    ev.preventDefault();
    appContext.setShowGlassLoading(true);

    const putObj = planningData.patchBody({
      key,
      payload: {
        ...stateCreateOrEdit,
        contentId,
        id: stateCreateOrEdit?.id,
        delivery: stateCreateOrEdit.planning.delivery,
        release: stateCreateOrEdit.planning.release,
        channelNotes: stateCreateOrEdit.planning.channelNotes,
        productionAssessment: stateCreateOrEdit.planning.productionAssessment,
        contentAssessment: stateCreateOrEdit.planning.contentAssessment,
        diversityAssessment: stateCreateOrEdit.planning.diversityAssessment,
      },
    });

    patchService({
      endpoint: `${planningData.endpoints.put({ key })}/${putObj.id}`,
      patchData: putObj,
      api: "v2",
    })
      .then(() => {
        appContext.setShowGlassLoading(false);
        toastSuccess(planningData.messages.putMessages.success);
        appContext.setCurrentStepBox("closed");
        getPlanning();
      })
      .catch(() => {
        appContext.setShowGlassLoading(false);
        toastError(planningData.messages.putMessages.error);
      });
  };

  const setAction = {
    create: (ev, refreshOnSuccess) => handleCreateSubmit(ev, refreshOnSuccess),
    edit: (ev) => handleEditSubmit(ev),
  };

  useEffect(() => {
    return () => {
      resetCreateOrEdit();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleIsSubmitValid = () => {
    if (["block", "material"].includes(stateCreateOrEdit.editType)) {
      return (
        !!stateCreateOrEdit.planning.delivery ||
        !!stateCreateOrEdit.planning.release ||
        !!stateCreateOrEdit.planning.contentAssessment ||
        !!stateCreateOrEdit.planning.diversityAssessment ||
        !!stateCreateOrEdit.planning.productionAssessment ||
        !!stateCreateOrEdit.planning.channelNotes
      );
    }

    if (stateCreateOrEdit.editType === "create") {
      return (
        !!stateCreateOrEdit.typeId &&
        (!!stateCreateOrEdit.planning.delivery ||
          !!stateCreateOrEdit.planning.release ||
          !!stateCreateOrEdit.planning.contentAssessment ||
          !!stateCreateOrEdit.planning.diversityAssessment ||
          !!stateCreateOrEdit.planning.productionAssessment ||
          !!stateCreateOrEdit.planning.channelNotes)
      );
    }
  };

  return (
    <div id="CreateAndUpdateBlockPlanning">
      <FormAndFields
        action={action}
        handleChangeByInputName={handleChangeByInputName}
        handleSubmit={setAction[action]}
        handleStartDates={handleStartDates}
        handleMaxDates={handleMaxDates}
        values={stateCreateOrEdit}
        handleValidate={handleIsSubmitValid}
        planningData={planningData}
        isMaterial={!isChapter}
        schedulerType={schedulerType}
        list={list}
        maxChaptersByBlock={maxChaptersByBlock}
      />
    </div>
  );
};

export default CreateAndUpdateMaterialsPlanning;
